import { createTheme } from '@mui/material/styles';
import { lime } from '@mui/material/colors';
import { error, neutral, primary, secondary } from './colors';
export const lightTheme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    spacing: 4,
    palettes: {
        mode: 'light',
    },
    typography: {
        fontFamily: ['Inter', 'Roboto', '-apple-system', 'sans-serif'].join(','),
        subtitle1: {
            fontSize: 12,
        },
        subtitle2: {
            fontSize: 10,
        },
    },
    components: {
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                },
                labelPlacementTop: {
                    alignItems: 'start',
                },
                label: {
                    fontSize: 12,
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                separator: {
                    marginLeft: 6,
                    marginRight: 6,
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                subtitle1: {
                    fontSize: 12,
                },
                subtitle2: {
                    fontSize: 10,
                    lineHeight: '12px',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                    padding: '8px 12px',
                    textTransform: 'none',
                    lineHeight: '20px',
                    borderRadius: 12,
                    borderColor: primary[40],
                    color: primary[40],
                },
                containedPrimary: {
                    background: primary[50],
                    color: primary[100],
                    '&:hover': {
                        background: primary[60],
                    },
                },
                containedError: {
                    background: error[50],
                    color: error[100],
                    '&:hover': {
                        background: error[60],
                    },
                },
                outlinedError: {
                    color: error[50],
                    borderColor: error[50],
                    '&:hover': {
                        borderColor: error[60],
                    },
                },
                sizeSmall: {
                    height: 24,
                    fontSize: 10,
                },
            },
        },
        MuiButtonGroup: {
            styleOverrides: {
                contained: {
                    button: {
                        color: primary[50],
                        background: primary[95],
                        border: '1px solid',
                        borderLeft: 'none',
                        '&:first-child': {
                            borderLeft: '1px solid',
                        },
                        '&:hover': {
                            background: primary[98],
                        },
                    },
                },
                middleButton: {
                    borderColor: primary[70],
                },
                firstButton: {
                    borderColor: primary[70],
                },
                lastButton: {
                    borderColor: primary[70],
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '& .MuiFormControl-root .MuiInputBase-root': {
                        flexWrap: 'nowrap',
                        overflowX: 'auto',
                        justifyContent: 'space-around',
                        gap: '4px',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    height: 'fit-content',
                    borderColor: neutral[80],
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    background: secondary[100],
                    height: 38,
                    '&.Mui-focused fieldset': {
                        borderColor: `${secondary[80]} !important`,
                    },
                    '&:hover fieldset': {
                        borderColor: `${secondary[80]} !important`,
                        borderWidth: 2,
                    },
                },
                input: {
                    fontSize: 12,
                    color: neutral[30],
                    '&::placeholder': {
                        color: neutral[70],
                    },
                },
                multiline: {
                    height: 'auto',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    fontSize: '12px',
                    color: neutral[30],
                    padding: '10px 8px',
                    lineHeight: '17px',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        opacity: 0.9,
                    },
                },
                sizeSmall: {
                    height: 20,
                    fontSize: 10,
                    lineHeight: '12px',
                    margin: '0 3px !important',
                },
                colorError: {
                    backgroundColor: error[95],
                    color: error[50],
                },
                deleteIcon: {
                    paddingLeft: '2px',
                    paddingRight: '4px',
                },
                filledPrimary: {
                    backgroundColor: primary[95],
                    color: primary[40],
                },
                // @ts-ignore
                deleteIconFilledColorError: {
                    stroke: error[50],
                },
                // @ts-ignore
                filledError: {
                    backgroundColor: error[95],
                    color: error[40],
                },
                colorWarning: {
                    backgroundColor: secondary[95],
                    color: secondary[40],
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: '62px',
                    height: '36px',
                    '&.MuiSwitch-sizeSmall': {
                        width: '50px',
                        height: '26px',
                    },
                    '&.MuiSwitch-sizeSmall .MuiSwitch-switchBase': {
                        paddingTop: '8px',
                        paddingLeft: '9px',
                    },
                    '&.MuiSwitch-sizeMedium .MuiSwitch-switchBase': {
                        paddingTop: '14px',
                        paddingLeft: '13px',
                    },
                },
                colorPrimary: {
                    '&.Mui-checked': {
                        color: '#FFFFFF',
                    },
                },
                track: {
                    opacity: '1 !important',
                    borderRadius: '12px',
                    backgroundColor: `${neutral[80]} !important`,
                    height: '20px',
                    width: '42px',
                    '&.small': {
                        width: '28px',
                        height: '16px',
                    },
                    '.Mui-checked.Mui-checked + &': {
                        backgroundColor: `${primary[40]} !important`,
                    },
                },
                thumb: {
                    color: '#FFFFFF',
                    borderRadius: '50%',
                    transform: 'translateX(0)',
                    width: '16px',
                    height: '16px',
                    '&.small': {
                        width: '14px',
                        height: '14px',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${neutral[80]}`,
                },
                indicator: {
                    background: primary[60],
                    height: 3,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontSize: 12,
                    color: neutral[30],
                    fontWeight: 600,
                    '&.Mui-selected': {
                        backgroundColor: neutral[98],
                    },
                },
                textColorPrimary: {
                    paddingLeft: '16px !important',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    flexDirection: 'column',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    paddingLeft: '16px !important',
                },
            },
        },
        MUIDataTableToolbar: {
            styleOverrides: {
                titleText: {
                    weight: 600,
                    fontSize: '12px',
                },
            },
        },
        MUIDataTable: {
            styleOverrides: {},
        },
        MUIDataTableBodyRow: {
            styleOverrides: {
                root: {
                    '&.mui-row-selected': {
                        background: primary[100],
                    },
                },
            },
        },
        MUIDataTableBodyCell: {
            styleOverrides: {
                stackedParent: {
                    fontSize: 12,
                    borderBottomColor: neutral[80],
                    lineHeight: '15px',
                    padding: '8px 16px',
                },
                root: {
                    '@media (max-width: 768px)': {
                        maxWidth: '170px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                },
            },
        },
        MUIDataTableHeadRow: {
            styleOverrides: {
                root: {},
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                root: {
                    background: neutral[98],
                    fontSize: '12px !important',
                    padding: '2px 16px !important',
                    '&:first-child': {
                        borderTopLeftRadius: '8px',
                    },
                    '&:last-child': {
                        borderTopRightRadius: '8px',
                    },
                },
            },
        },
        MUIDataTableSelectCell: {
            styleOverrides: {
                root: {
                    width: '0px',
                    '&:not(.MuiTableCell-head)': {
                        borderBottomColor: neutral[80],
                    },
                },
                headerCell: {
                    background: '#F4F8FA',
                    borderTopLeftRadius: '8px',
                },
                checkboxRoot: {
                    padding: '4px 9px',
                    '& svg': {
                        fontSize: '20px',
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                list: {
                    padding: 12,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    padding: '6px 8px',
                    borderRadius: '8px',
                    '&:hover': {
                        backgroundColor: primary[95],
                    },
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    '@media (max-width: 780px)': {
                        minHeight: 28,
                    },
                },
            },
        },
        MuiPagination: {
            styleOverrides: {
                ul: {
                    gap: 2,
                },
            },
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    minWidth: 23,
                    fontSize: 10,
                    '&.Mui-selected': {
                        background: primary[90],
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&.MuiTableRow-hover:hover': {
                        backgroundColor: primary[98],
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    '& .MuiButtonBase-root': {
                        fontSize: '12px!important',
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    '&:before': {
                        display: 'none',
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                message: {
                    width: '100%',
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    padding: '12px',
                },
            },
        },
    },
});
export const darkTheme = createTheme({
    palette: {
        primary: lime,
        secondary: lime,
        mcdYellow: '#FFC425',
    },
});
export default lightTheme;
